import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Container,
  useTheme,
  useMediaQuery,
  Tooltip,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import MapIcon from '@mui/icons-material/Map';
import CategoryIcon from '@mui/icons-material/Category';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StorageIcon from '@mui/icons-material/Storage';
import GridViewIcon from '@mui/icons-material/GridView';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../../accounting/shared/StatsCard';
import SmartWarehouseModal from '../components/SmartWarehouseModal';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface SmartWarehouse {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  responsiblePerson?: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  branchesId?: string;
  warehouseType: string;
  locationModel?: string;
  totalCapacity?: number;
  defaultRoutingStrategy?: string;
  createdAt: string;
  updatedAt?: string;
  locationCount: number;
  activeLocationCount: number;
  totalProducts: number;
  inStockItemCount: number;
  totalStockQuantity: number;
}

const WmsWarehouseSetup: React.FC = () => {
  const { t } = useTranslation('warehouse');
  const theme = useTheme();
  const getLocationModelLabel = (model?: string) => {
    if (!model) return '-';
    return t(`lookup.locationModel.${model}`, { defaultValue: model });
  };

  const getRoutingLabel = (strategy?: string) => {
    if (!strategy) return '-';
    return t(`lookup.routingStrategy.${strategy}`, { defaultValue: strategy });
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [warehouses, setWarehouses] = useState<SmartWarehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<SmartWarehouse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ✅ Server-side aggregate: SmartWarehouseController.GetAll endpoint'i
  // her depo için Quantity > 0 filtresiyle TotalStockQuantity ve InStockItemCount döner.
  // "Depo Ürünleri" ekranıyla aynı tablo (WarehouseStocks) ve aynı filtre kullanılır.
  const smartStats = React.useMemo(() => {
    const totalStockQuantity = warehouses.reduce((sum, w) => sum + (w.totalStockQuantity || 0), 0);
    const totalItemCount = warehouses.reduce((sum, w) => sum + (w.inStockItemCount || 0), 0);
    const totalLocations = warehouses.reduce((sum, w) => sum + (w.locationCount || 0), 0);
    const activeLocations = warehouses.reduce((sum, w) => sum + (w.activeLocationCount || 0), 0);
    return {
      totalStockQuantity,
      totalItemCount,
      totalLocations,
      activeLocations,
      activeWarehouseCount: warehouses.filter((w) => w.isActive).length,
      warehouseCount: warehouses.length,
    };
  }, [warehouses]);

  const loadWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[WMS] Akıllı depolar yükleniyor...');
      const response = await smartWarehouseService.getAll();
      console.log('[WMS] API yanıt:', response.status, response.data);
      const data = response.data?.Data || response.data?.data || [];

      const mapped: SmartWarehouse[] = data.map((w: any) => ({
        id: w.Id || w.id,
        name: w.Name || w.name,
        address: w.Address || w.address,
        phone: w.Phone || w.phone,
        responsiblePerson: w.ResponsiblePerson || w.responsiblePerson,
        description: w.Description || w.description,
        isDefault: w.IsDefault ?? w.isDefault ?? false,
        isActive: w.IsActive ?? w.isActive ?? true,
        branchesId: w.BranchesId || w.branchesId,
        warehouseType: w.WarehouseType || w.warehouseType || 'Smart',
        locationModel: w.LocationModel || w.locationModel,
        totalCapacity: w.TotalCapacity ?? w.totalCapacity,
        defaultRoutingStrategy: w.DefaultRoutingStrategy || w.defaultRoutingStrategy,
        createdAt: w.CreatedAt || w.createdAt,
        updatedAt: w.UpdatedAt || w.updatedAt,
        locationCount: w.LocationCount ?? w.locationCount ?? 0,
        activeLocationCount: w.ActiveLocationCount ?? w.activeLocationCount ?? 0,
        totalProducts: w.TotalProducts ?? w.totalProducts ?? 0,
        inStockItemCount: Number(w.InStockItemCount ?? w.inStockItemCount ?? 0),
        totalStockQuantity: Number(w.TotalStockQuantity ?? w.totalStockQuantity ?? 0),
      }));

      console.log('[WMS] Yüklenen depo sayısı:', mapped.length);
      setWarehouses(mapped);
    } catch (err: any) {
      const respData = err?.response?.data;
      const status = err?.response?.status;
      const url = err?.config?.url;
      console.error('[WMS] Depo yükleme hatası:', { status, url, data: respData, message: err.message });
      setError(t('pages.warehouseSetup.loadingFailed', { status: status || 'network', url: url || '' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (warehouse: SmartWarehouse) => {
    setEditData({
      Id: warehouse.id,
      Name: warehouse.name,
      Address: warehouse.address,
      Phone: warehouse.phone,
      ResponsiblePerson: warehouse.responsiblePerson,
      Description: warehouse.description,
      IsDefault: warehouse.isDefault,
      BranchesId: warehouse.branchesId,
      LocationModel: warehouse.locationModel,
      TotalCapacity: warehouse.totalCapacity,
      DefaultRoutingStrategy: warehouse.defaultRoutingStrategy,
    });
    setModalOpen(true);
  };

  const handleDeleteClick = (warehouse: SmartWarehouse, event: React.MouseEvent) => {
    event.stopPropagation();
    setWarehouseToDelete(warehouse);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!warehouseToDelete) return;
    try {
      setIsDeleting(true);
      await smartWarehouseService.delete(warehouseToDelete.id);
      setSnackbar({ open: true, message: t('pages.warehouseSetup.deletedSuccess'), severity: 'success' });
      setDeleteDialogOpen(false);
      setWarehouseToDelete(null);
      await loadWarehouses();
    } catch (err: any) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || t('pages.warehouseSetup.deleteFailed');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaved = () => {
      setSnackbar({ open: true, message: editData ? t('pages.warehouseSetup.updatedSuccess') : t('pages.warehouseSetup.createdSuccess'), severity: 'success' });
    loadWarehouses();
  };

  const handleManageLocations = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/wms/depo-tanimlama/${id}`);
  };

  const columns: DataTableColumn<SmartWarehouse>[] = [
    {
      id: 'name',
      label: t('pages.warehouseSetup.name'),
      field: 'name',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarehouseIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
        </Box>
      ),
    },
    {
      id: 'locationModel',
      label: t('pages.warehouseSetup.locationModel'),
      sortable: true,
      render: (row) => (
        <Typography variant="body2">
          {getLocationModelLabel(row.locationModel)}
        </Typography>
      ),
    },
    {
      id: 'routingStrategy',
      label: t('pages.warehouseSetup.routingStrategy'),
      sortable: true,
      render: (row) => (
        <Typography variant="body2">
          {getRoutingLabel(row.defaultRoutingStrategy)}
        </Typography>
      ),
    },
    {
      id: 'locationCount',
      label: t('pages.warehouseSetup.locationCount'),
      sortable: true,
      width: '100px',
      align: 'center',
      render: (row) => (
        <Typography variant="body2" fontWeight="bold">{row.locationCount}</Typography>
      ),
    },
    {
      id: 'status',
      label: t('common.status'),
      sortable: false,
      width: '100px',
      align: 'center',
      render: (row) => (
        <Chip
          label={row.isActive ? t('common.active') : t('common.passive')}
          size="small"
          color={row.isActive ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'actions',
      label: t('pages.warehouseSetup.actions'),
      width: '160px',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title={t('pages.warehouseSetup.manageLocations')}>
            <IconButton size="small" onClick={(e) => handleManageLocations(row.id, e)}>
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('pages.warehouseSetup.edit')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('pages.warehouseSetup.delete')}>
            <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(row, e)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <StatsCardsContainer loading={loading && warehouses.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.blue}
            title={`${smartStats.totalStockQuantity}`}
            subtitle={t('pages.warehouseSetup.statsTotalStock', { defaultValue: 'Toplam Stok Miktarı' })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<Inventory2Icon />}
            color={STATS_COLORS.orange}
            title={`${smartStats.totalItemCount}`}
            subtitle={t('pages.warehouseSetup.statsItemCount', { defaultValue: 'Ürün Çeşidi' })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<StorageIcon />}
            color={STATS_COLORS.green}
            title={`${smartStats.activeWarehouseCount} / ${smartStats.warehouseCount}`}
            subtitle={t('pages.warehouseSetup.statsWarehouseCount', { defaultValue: 'Aktif Akıllı Depo' })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<GridViewIcon />}
            color={STATS_COLORS.purple}
            title={`${smartStats.activeLocations} / ${smartStats.totalLocations}`}
            subtitle={t('pages.warehouseSetup.statsLocationCount', { defaultValue: 'Aktif Lokasyon' })}
          />
        </Grid>
      </StatsCardsContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
            {t('pages.warehouseSetup.title')}
          </Typography>
          <GuideButton guideKey="wms.warehouseSetup" />
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t('pages.warehouseSetup.addWarehouse')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : warehouses.length === 0 ? (
        <Box sx={{
          p: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2,
        }}>
          <WarehouseIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('pages.warehouseSetup.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {t('pages.warehouseSetup.emptyDescription')}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('pages.warehouseSetup.addFirstWarehouse')}
          </Button>
        </Box>
      ) : (
        <>
          {!isMobile && (
            <AccountingDataTable<SmartWarehouse>
              columns={columns}
              rows={warehouses}
              getRowId={(row) => row.id}
              loading={false}
              pagination={false}
              emptyMessage={t('pages.warehouseSetup.emptyMessage')}
              ariaLabel={t('pages.warehouseSetup.tableAriaLabel')}
              onRowClick={(row) => handleEdit(row)}
              clickable
              columnResizeStorageKey="wms-warehouse"
            />
          )}

          {isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {warehouses.map((warehouse) => (
                <Card
                  key={warehouse.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleEdit(warehouse)}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <WarehouseIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {warehouse.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={warehouse.isActive ? t('common.active') : t('common.passive')}
                        size="small"
                        color={warehouse.isActive ? 'success' : 'default'}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Chip
                        label={getLocationModelLabel(warehouse.locationModel)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={getRoutingLabel(warehouse.defaultRoutingStrategy)}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={t('pages.warehouseSetup.mobileLocationsChip', { count: warehouse.locationCount })}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<MapIcon />}
                      onClick={(e) => handleManageLocations(warehouse.id, e)}
                    >
                      {t('pages.warehouseSetup.mobileLocationsButton')}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={(e) => handleDeleteClick(warehouse, e)}
                    >
                      {t('pages.warehouseSetup.delete')}
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <SmartWarehouseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        editData={editData}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">{t('pages.warehouseSetup.deleteConfirmTitle')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('pages.warehouseSetup.deleteWarning')}
          </Alert>
          <Typography variant="body1" gutterBottom>
            {t('pages.warehouseSetup.deleteQuestion', { name: warehouseToDelete?.name || '' })}
          </Typography>
          {warehouseToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {t('pages.warehouseSetup.detailsTitle')}
              </Typography>
              <Typography variant="body2">{t('pages.warehouseSetup.detailsName', { name: warehouseToDelete.name })}</Typography>
              {warehouseToDelete.locationModel && (
                <Typography variant="body2">
                  {t('pages.warehouseSetup.detailsModel', { model: getLocationModelLabel(warehouseToDelete.locationModel) })}
                </Typography>
              )}
              <Typography variant="body2">{t('pages.warehouseSetup.detailsCount', { count: warehouseToDelete.locationCount })}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting} variant="outlined">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('pages.warehouseSetup.deleteLoading') : t('pages.warehouseSetup.deleteConfirmAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WmsWarehouseSetup;
